import { useAtom, useSetAtom } from 'jotai';
import Axios, { AxiosResponse } from 'axios';
import { environment } from '../../environment';
import { errorCatcher, snackbarSettingsAtom } from '../../hooks';
import { useLogin } from '../login';
import { IFeatureFlag, featureFlagsAtom, featureFlagsLoadingAtom } from './types';

type ListFlags = () => Promise<void>;
type CreateFlag = (payload: IFeatureFlag) => Promise<IFeatureFlag | null>;
type UpdateFlag = (payload: IFeatureFlag) => Promise<IFeatureFlag | null>;
type DeleteFlag = (id: number) => Promise<boolean>;

type FeatureFlagKeyFunctions = {
    list: ListFlags;
    create: CreateFlag;
    update: UpdateFlag;
    remove: DeleteFlag;
}

type UseFeatureFlagsService = [
    IFeatureFlag[],
    boolean,
    FeatureFlagKeyFunctions
];

export const useFeatureFlagsService = (): UseFeatureFlagsService => {
    const [flags, setFlags] = useAtom(featureFlagsAtom);
    const [loading, setLoading] = useAtom(featureFlagsLoadingAtom);
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const list: ListFlags = async () => {
        setLoading(true);
        const response: AxiosResponse<IFeatureFlag[]> | void = await Axios.get(`${environment.authenticationUrl}/feature-flags`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setFlags(response.data || []);
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to load feature flags' });
        }
        setLoading(false);
    }

    const create: CreateFlag = async (payload: IFeatureFlag) => {
        const response: AxiosResponse<IFeatureFlag> | void = await Axios.post(`${environment.authenticationUrl}/feature-flags`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Feature flag created' });
            await list();
            return response.data;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to create feature flag' });
            return null;
        }
    }

    const update: UpdateFlag = async (payload: IFeatureFlag) => {
        const response: AxiosResponse<IFeatureFlag> | void = await Axios.put(`${environment.authenticationUrl}/feature-flags/${payload.id}`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Feature flag updated' });
            await list();
            return response.data;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update feature flag' });
            return null;
        }
    }

    const remove: DeleteFlag = async (id: number) => {
        const response: AxiosResponse<boolean> | void = await Axios.delete(`${environment.authenticationUrl}/feature-flags/${id}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Feature flag deleted' });
            await list();
            return true;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to delete feature flag' });
            return false;
        }
    }

    return [flags, loading, { list, create, update, remove }];
}


